import React, { useMemo, useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../components/HeroSection';
import { Search, Calendar, User, ArrowRight, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: 'essay' | 'reporting' | 'poetry' | 'visual' | 'interview';
  readTime: string;
  featured?: boolean;
  image?: string;
}
export function Blog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const posts: BlogPost[] = [{
    slug: 'architecture-of-exclusion',
    title: 'The Architecture of Exclusion: How Public Spaces Are Designed to Police Fat Bodies',
    excerpt: "From subway turnstiles to restaurant booths, the built environment tells fat people we don't belong. This essay examines how hostile architecture operates as a form of social control.",
    author: 'Jordan Miller',
    date: '2024-01-15',
    category: 'essay',
    readTime: '12 min read',
    featured: true
  }, {
    slug: 'hunger-as-resistance',
    title: 'Hunger as Resistance',
    excerpt: 'A collection of poems reclaiming appetite in a world that demands our starvation.',
    author: 'Sam Rivera',
    date: '2024-01-10',
    category: 'poetry',
    readTime: '8 min read'
  }, {
    slug: 'beyond-representation',
    title: "Beyond Representation: Why Seeing Fat People on TV Isn't Enough",
    excerpt: 'Visibility without structural change is just better PR for the same oppressive systems.',
    author: 'Alex Chen',
    date: '2024-01-05',
    category: 'essay',
    readTime: '15 min read',
    featured: true
  }, {
    slug: 'bariatric-surgery-industry',
    title: 'Inside the Bariatric Surgery Industry: An Investigation',
    excerpt: 'How the weight loss surgery industry profits from desperation while downplaying risks and long-term complications.',
    author: 'Taylor Kim',
    date: '2023-12-20',
    category: 'reporting',
    readTime: '20 min read'
  }, {
    slug: 'fat-joy-photo-series',
    title: 'Fat Futures: A Visual Essay',
    excerpt: 'A photographic exploration of fat joy, community, and radical imagination.',
    author: 'Riley Martinez',
    date: '2023-12-15',
    category: 'visual',
    readTime: '5 min read'
  }, {
    slug: 'disability-justice-conversation',
    title: 'Fat & Disabled: A Conversation',
    excerpt: 'Three fat disabled activists discuss navigating medical systems, accessibility, and the intersection of fatphobia and ableism.',
    author: 'Multiple Authors',
    date: '2023-12-10',
    category: 'interview',
    readTime: '18 min read'
  }, {
    slug: 'schools-weaponize-bmi',
    title: 'How Schools Weaponize BMI: An Investigation',
    excerpt: "BMI report cards, mandatory weigh-ins, and the surveillance of children's bodies in American schools.",
    author: 'Morgan Lee',
    date: '2023-12-01',
    category: 'reporting',
    readTime: '16 min read'
  }, {
    slug: 'body-as-archive',
    title: 'Body as Archive',
    excerpt: 'A long-form poem exploring memory, inheritance, and what our bodies hold.',
    author: 'Casey Johnson',
    date: '2023-11-25',
    category: 'poetry',
    readTime: '6 min read'
  }];
  const categories = [{
    id: 'all',
    label: 'All Posts',
    color: 'bg-brand-orange'
  }, {
    id: 'essay',
    label: 'Essays',
    color: 'bg-brand-magenta'
  }, {
    id: 'reporting',
    label: 'Reporting',
    color: 'bg-brand-cyan'
  }, {
    id: 'poetry',
    label: 'Poetry',
    color: 'bg-brand-lime'
  }, {
    id: 'visual',
    label: 'Visual Essays',
    color: 'bg-brand-orange'
  }, {
    id: 'interview',
    label: 'Interviews',
    color: 'bg-brand-navy'
  }];
  const filteredPosts = useMemo(() => {
    let filtered = posts;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    if (searchQuery) {
      filtered = filtered.filter(post => post.title.toLowerCase().includes(searchQuery.toLowerCase()) || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) || post.author.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return filtered;
  }, [searchQuery, selectedCategory]);
  const featuredPosts = posts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);
  const getCategoryColor = (category: string) => {
    const colors = {
      essay: 'text-brand-magenta',
      reporting: 'text-brand-cyan',
      poetry: 'text-brand-lime',
      visual: 'text-brand-orange',
      interview: 'text-brand-navy'
    };
    return colors[category as keyof typeof colors];
  };
  const getCategoryBorder = (category: string) => {
    const colors = {
      essay: 'border-brand-magenta',
      reporting: 'border-brand-cyan',
      poetry: 'border-brand-lime',
      visual: 'border-brand-orange',
      interview: 'border-brand-navy'
    };
    return colors[category as keyof typeof colors];
  };
  return <div className="min-h-screen bg-brand-cream" data-id="element-313">
      <HeroSection title="The Blog" subtitle="Essays, reporting, poetry, and visual work from fat creators." tag="The Digital Publication" bgColor="orange" data-id="element-314" />

      {/* Search & Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto -mt-8 bg-white relative z-10 shadow-lg border-2 border-brand-black" data-id="element-315">
        <div className="space-y-6" data-id="element-316">
          <div className="relative" data-id="element-317">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-318" />
            <input type="text" placeholder="Search posts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 focus:border-brand-orange focus:outline-none text-lg" data-id="element-319" />
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-320">
            <div className="flex items-center gap-2 text-sm font-bold uppercase text-gray-600" data-id="element-321">
              <Filter className="h-4 w-4" data-id="element-322" />
              <span data-id="element-323">Category:</span>
            </div>
            {categories.map(category => <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all border-2 ${selectedCategory === category.id ? `${category.color} text-white border-transparent` : 'bg-white text-gray-600 border-gray-200 hover:border-brand-orange'}`} data-id="element-324">
                {category.label}
              </button>)}
          </div>

          <div className="text-sm text-gray-500" data-id="element-325">
            {filteredPosts.length}{' '}
            {filteredPosts.length === 1 ? 'post' : 'posts'}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {selectedCategory === 'all' && !searchQuery && <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-326">
          <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-327">
            Featured
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-328">
            {featuredPosts.map((post, index) => <Link key={post.slug} to={`/blog/${post.slug}`} className="group" data-id="element-329">
                <motion.article initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="border-2 border-brand-black bg-white hover:shadow-2xl transition-all" data-id="element-330">
                  <div className="aspect-[16/9] bg-brand-orange/20 border-b-2 border-brand-black relative overflow-hidden" data-id="element-331">
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/30 to-brand-magenta/30 group-hover:scale-105 transition-transform duration-300" data-id="element-332"></div>
                  </div>
                  <div className="p-8" data-id="element-333">
                    <div className="flex items-center gap-3 mb-4" data-id="element-334">
                      <span className={`text-xs font-bold uppercase tracking-wider ${getCategoryColor(post.category)}`} data-id="element-335">
                        {post.category}
                      </span>
                      <span className="text-gray-300" data-id="element-336">•</span>
                      <span className="text-xs text-gray-500" data-id="element-337">
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-2xl font-black mb-4 text-brand-black group-hover:text-brand-orange transition-colors" data-id="element-338">
                      {post.title}
                    </h3>
                    <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-339">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100" data-id="element-340">
                      <div className="flex items-center gap-3 text-sm" data-id="element-341">
                        <User className="h-4 w-4 text-gray-400" data-id="element-342" />
                        <span className="font-medium" data-id="element-343">{post.author}</span>
                        <span className="text-gray-300" data-id="element-344">•</span>
                        <Calendar className="h-4 w-4 text-gray-400" data-id="element-345" />
                        <span className="text-gray-500" data-id="element-346">
                          {new Date(post.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                        </span>
                      </div>
                      <ArrowRight className="h-5 w-5 text-brand-orange group-hover:translate-x-1 transition-transform" data-id="element-347" />
                    </div>
                  </div>
                </motion.article>
              </Link>)}
          </div>
        </section>}

      {/* All Posts Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-348">
        {selectedCategory === 'all' && !searchQuery && <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-349">
            Recent Posts
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-350">
          {regularPosts.map((post, index) => <Link key={post.slug} to={`/blog/${post.slug}`} className="group" data-id="element-351">
              <motion.article initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} className={`border-l-4 ${getCategoryBorder(post.category)} bg-white p-6 hover:shadow-lg transition-shadow h-full flex flex-col`} data-id="element-352">
                <div className="flex items-center gap-2 mb-3" data-id="element-353">
                  <span className={`text-xs font-bold uppercase tracking-wider ${getCategoryColor(post.category)}`} data-id="element-354">
                    {post.category}
                  </span>
                  <span className="text-gray-300" data-id="element-355">•</span>
                  <span className="text-xs text-gray-500" data-id="element-356">{post.readTime}</span>
                </div>

                <h3 className="text-xl font-black mb-3 text-brand-black group-hover:text-brand-orange transition-colors" data-id="element-357">
                  {post.title}
                </h3>

                <p className="text-gray-700 text-sm mb-4 leading-relaxed flex-grow" data-id="element-358">
                  {post.excerpt}
                </p>

                <div className="flex items-center gap-2 text-xs text-gray-500 pt-4 border-t border-gray-100" data-id="element-359">
                  <User className="h-3 w-3" data-id="element-360" />
                  <span data-id="element-361">{post.author}</span>
                  <span className="text-gray-300" data-id="element-362">•</span>
                  <Calendar className="h-3 w-3" data-id="element-363" />
                  <span data-id="element-364">
                    {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
                  </span>
                </div>
              </motion.article>
            </Link>)}
        </div>

        {filteredPosts.length === 0 && <div className="text-center py-16" data-id="element-365">
            <p className="text-xl text-gray-500 mb-4" data-id="element-366">No posts found</p>
            <button onClick={() => {
          setSearchQuery('');
          setSelectedCategory('all');
        }} className="text-brand-orange font-bold hover:underline" data-id="element-367">
              Clear filters
            </button>
          </div>}
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-brand-orange text-white" data-id="element-368">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-369">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-370">
            Never Miss a Post
          </h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-371">
            Get new essays, reporting, and poetry delivered to your inbox
            monthly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-372">
            <input type="email" placeholder="Your email" className="flex-1 px-4 py-3 text-brand-black border-2 border-white focus:outline-none" data-id="element-373" />
            <button className="px-8 py-3 bg-white text-brand-orange font-bold uppercase tracking-wide hover:bg-gray-100 transition-colors" data-id="element-374">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>;
}